import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ContactForm from './ContactForm';

/* ── Blockchain floating blocks background ── */
function BlockchainBg() {
  const blocks = useMemo(() => {
    const items = [];
    for (let i = 0; i < 18; i++) {
      const seed = (i + 1) * 7919;
      items.push({
        x: (seed * 13) % 100,
        y: (seed * 17) % 100,
        w: 60 + (seed % 60),
        h: 28 + (seed % 18),
        delay: (i * 0.4) % 6,
        dur: 12 + (seed % 10),
      });
    }
    return items;
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <svg className="absolute inset-0 w-full h-full opacity-[0.04]">
        {blocks.slice(0, 12).map((b, i) => {
          const next = blocks[(i + 3) % blocks.length];
          return (
            <line key={i} x1={`${b.x}%`} y1={`${b.y}%`} x2={`${next.x}%`} y2={`${next.y}%`}
              stroke="#A84397" strokeWidth="1" strokeDasharray="6 4" />
          );
        })}
      </svg>
      {blocks.map((b, i) => (
        <motion.div
          key={i}
          className="absolute rounded-md border"
          style={{
            left: `${b.x}%`, top: `${b.y}%`,
            width: b.w, height: b.h,
            borderColor: 'rgba(168, 67, 151, 0.12)',
            backgroundColor: 'rgba(168, 67, 151, 0.03)',
          }}
          animate={{ y: [0, -15, 0, 10, 0], x: [0, 6, 0, -6, 0], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: b.dur, delay: b.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
      {blocks.slice(0, 8).map((b, i) => (
        <motion.div
          key={`dot-${i}`}
          className="absolute w-1.5 h-1.5 rounded-full"
          style={{
            left: `calc(${b.x}% + ${b.w}px)`,
            top: `${b.y}%`,
            backgroundColor: '#D23CB3',
            boxShadow: '0 0 6px rgba(210,60,179,0.5)',
          }}
          animate={{ opacity: [0.2, 0.8, 0.2], scale: [0.8, 1.3, 0.8] }}
          transition={{ duration: 3 + i * 0.5, delay: b.delay, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
    </div>
  );
}

export default function Contact() {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-transparent relative overflow-hidden" id="contact">
      <BlockchainBg />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-primary/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Columna izquierda — texto */}
          <div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-headline tracking-tight leading-tight">
              {t('contact.sectionTitle')}{' '}
              <span className="gradient-text">{t('contact.sectionTitleHighlight')}</span>
            </h2>
            <p className="text-on-surface-variant text-base sm:text-lg mt-6 leading-relaxed">
              {t('contact.sectionSubtitle')}
            </p>
            <div className="flex items-center gap-2 mt-8 text-on-surface-variant/80">
              <span className="material-symbols-outlined text-purple-primary">shield</span>
              <span className="text-sm font-medium">{t('contact.security')}</span>
            </div>
          </div>

          {/* Columna derecha — formulario */}
          <ContactForm
            className="p-5 sm:p-8 bg-[#1a1a1a] border border-outline-variant/30 rounded-2xl"
          />
        </div>
      </div>
    </section>
  );
}
